import PropTypes from "prop-types";
import { useState } from "react";
import { Modal as DSModal, ModalContent } from "@dextech-ai/dex-design-system";

// Width goes through the DS Modal's own `width` prop (number in px or a CSS length; DS default 495).
// `size="large"` is the 900 px shorthand for the legacy "large" modals; `width` sets any other legacy
// width explicitly (e.g. MEDIUM_MODAL_WIDTH, the legacy `medium` cap) and wins over `size`.
export const LARGE_MODAL_WIDTH = 900;
export const MEDIUM_MODAL_WIDTH = 666;

// The design-system Modal (antd Modal underneath) with the legacy `componentsDS/Modal` open / close
// props, so a caller swaps its import and its `CardConfigure` for the DS ModalHeader / ModalBody /
// ModalFooter slots. Apart from the width above, the DS design and behaviour are kept as they ship:
// no z-index or other overrides.
//
// QA hooks: qa-playwright finds modals by the legacy classes, so they ride on the matching antd
// layers (their legacy layout is reset in elementsDS/_ds-modal.scss):
//   .DS__modal          -> antd root, wraps the whole modal (`.DS__modal button:has-text(...)`)
//   .DS__modal__content -> antd wrap, the full-screen layer a click outside the dialog lands on
//   .DS__modal_box      -> antd dialog box
// `addClass` stays on the outer element, as before, so rules scoped under it still apply.
//
// Kept from the legacy shell, so callers behave as before:
// - Nothing is mounted while the modal is closed (the legacy shell rendered nothing when not
//   visible): the DS Modal is rendered as soon as `visible` turns true and removed once antd has
//   finished closing it (`afterClose`). Forms inside start fresh on each open, mount effects run
//   again, and a closed modal leaves no hidden buttons / ids — or its full-screen root — behind.
// - A click inside the dialog doesn't bubble to the component that renders the modal (React
//   bubbles through portals), so a card or row behind it doesn't react.
const stopClick = (e) => e.stopPropagation();

const Modal = ({ children, visible, close, size, width, dismissClickOutsideModal = true, addClass, dataTestId }) => {
	const [mounted, setMounted] = useState(!!visible);
	if (visible && !mounted) {
		setMounted(true);
	}
	if (!mounted) {
		return null;
	}

	return (
		<DSModal open={!!visible} onOpenChange={(open) => { if (!open) close?.(); }}>
			<ModalContent
				// Left unset for the default size, so the DS keeps its own width.
				width={width ?? (size === "large" ? LARGE_MODAL_WIDTH : undefined)}
				// The DS `maskClosable` / `keyboard` options: a modal that can't be dismissed by a click
				// outside can't be dismissed by Escape either.
				maskClosable={dismissClickOutsideModal}
				keyboard={dismissClickOutsideModal}
				afterClose={() => setMounted(false)}
				// Legacy: a modal that can't be dismissed also swallowed the click outside it.
				wrapProps={dismissClickOutsideModal ? undefined : { onClick: stopClick }}
				rootClassName={["DS__modal", addClass].filter(Boolean).join(" ")}
				wrapClassName="DS__modal__content"
				className="DS__modal_box"
				// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
				modalRender={(node) => <div data-testid={dataTestId} onClick={stopClick}>{node}</div>}
			>
				{children}
			</ModalContent>
		</DSModal>
	);
};

Modal.propTypes = {
	children: PropTypes.node,
	visible: PropTypes.bool,
	close: PropTypes.func,
	size: PropTypes.oneOf(["large"]),
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	dismissClickOutsideModal: PropTypes.bool,
	addClass: PropTypes.string,
	dataTestId: PropTypes.string,
};

export default Modal;
